import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { ConversionsDevelopersService, type ApiTokenFilter } from "#gen/conversions_developers/v1/service_pb.js";
import type { Secret, UserApiToken } from "#gen/conversions_developers/v1/token_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { APIError, handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface UserApiTokensList {
	userApiTokens: UserApiToken[];
	pageInfo?: PaginationPageInfo;
}

export class ConversionsDevelopersClient {
	private client: Client<typeof ConversionsDevelopersService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.conversionsDevelopers || "/", ConversionsDevelopersService, options);
	}

	async createUserApiToken(name: string, description?: string): Promise<UserApiToken> {
		try {
			const res = await this.client.createUserApiToken({
				name,
				description,
			});
			return res.userApiToken!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listUserApiTokens(filter?: ApiTokenFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<UserApiTokensList> {
		try {
			const res = await this.client.listUserApiTokens(
				{
					filter,
					paginate,
				},
				options,
			);
			return { userApiTokens: res.userApiTokens!, pageInfo: res.pageInfo };
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteUserApiToken(tokenId: string): Promise<void> {
		try {
			await this.client.deleteUserApiToken({ tokenId });
		} catch (e) {
			throw handleError(e);
		}
	}

	async viewUserApiTokenSecret(tokenId: string): Promise<Secret> {
		try {
			const res = await this.client.viewUserApiTokenSecret({
				tokenId,
			});
			if (res.secret) {
				return res.secret;
			}
			throw new APIError("unable to fetch token details");
		} catch (e) {
			throw handleError(e);
		}
	}
}
